import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ProductId,
  ProductVariantId,
  StockLocationId,
  StoreId,
} from "@KassaroAB/shared/types/ids";
import type { RestStockLocation } from "@KassaroAB/shared/types/stockLocation";
import type { StockPolicyAssignment } from "@KassaroAB/shared/types/stockPolicy";
import { useGetProductStockBalancesQuery } from "../../../query/stockBalancesQuery";

export interface VariantStockLocation {
  id: StockLocationId;
  name: string;
  label?: string;
}

export interface ProductStockChanges {
  balances: {
    stockLocationId: StockLocationId;
    variantId: ProductVariantId | null;
    quantity: number | null;
  }[];
  policies: {
    stockLocationId: StockLocationId;
    variantId: ProductVariantId | null;
    stockPolicy: StockPolicyAssignment | null;
  }[];
}

interface UseProductVariantStockParams {
  storeId: StoreId | null;
  productId: ProductId | undefined;
  defaultStockLocationId: StockLocationId | null;
  stockLocations: RestStockLocation[];
  enabled: boolean;
}

const cellKey = (
  stockLocationId: StockLocationId,
  variantId: ProductVariantId | null,
) => `${stockLocationId}|${variantId ?? ""}`;

export const useProductVariantStock = ({
  storeId,
  productId,
  defaultStockLocationId,
  stockLocations,
  enabled,
}: UseProductVariantStockParams) => {
  const { t } = useTranslation();
  const [quantityEdits, setQuantityEdits] = useState<
    Record<string, ProductStockChanges["balances"][number]>
  >({});
  const [policyEdits, setPolicyEdits] = useState<
    Record<string, ProductStockChanges["policies"][number]>
  >({});

  const { data: balances, isPending } = useGetProductStockBalancesQuery(
    enabled ? storeId : null,
    productId ?? null,
  );

  const stored = useMemo(
    () =>
      new Map(
        (balances ?? []).map((balance) => [
          cellKey(balance.stockLocationId, balance.variantId),
          balance,
        ]),
      ),
    [balances],
  );

  const locations = useMemo(
    (): VariantStockLocation[] =>
      enabled
        ? [...stockLocations]
            .sort(
              (a, b) =>
                Number(b.id === defaultStockLocationId) -
                Number(a.id === defaultStockLocationId),
            )
            .map((location) => ({
              id: location.id,
              name: location.name,
              label:
                location.id === defaultStockLocationId
                  ? t("productForm.variantsTable.thisStore")
                  : undefined,
            }))
        : [],
    [enabled, stockLocations, defaultStockLocationId, t],
  );

  const getQuantity = (
    stockLocationId: StockLocationId,
    variantId: ProductVariantId | null,
  ): number | null => {
    const key = cellKey(stockLocationId, variantId);
    return key in quantityEdits
      ? quantityEdits[key].quantity
      : (stored.get(key)?.quantity ?? null);
  };

  const setQuantity = (
    stockLocationId: StockLocationId,
    variantIds: (ProductVariantId | null)[],
    quantity: number | null,
  ) =>
    setQuantityEdits((previous) => {
      const next = { ...previous };
      variantIds.forEach((variantId) => {
        const key = cellKey(stockLocationId, variantId);
        if (quantity === (stored.get(key)?.quantity ?? null)) {
          delete next[key];
        } else {
          next[key] = { stockLocationId, variantId, quantity };
        }
      });
      return next;
    });

  const getPolicy = (
    stockLocationId: StockLocationId,
    variantId: ProductVariantId | null,
  ): StockPolicyAssignment | null => {
    const key = cellKey(stockLocationId, variantId);
    return key in policyEdits
      ? policyEdits[key].stockPolicy
      : (stored.get(key)?.stockPolicy ?? null);
  };

  const setPolicy = (
    stockLocationId: StockLocationId,
    variantIds: (ProductVariantId | null)[],
    update: (
      current: StockPolicyAssignment | null,
    ) => StockPolicyAssignment | null,
  ) =>
    setPolicyEdits((previous) => {
      const next = { ...previous };
      variantIds.forEach((variantId) => {
        const key = cellKey(stockLocationId, variantId);
        const storedPolicy = stored.get(key)?.stockPolicy ?? null;
        const stockPolicy = update(
          key in next ? next[key].stockPolicy : storedPolicy,
        );
        if (JSON.stringify(stockPolicy) === JSON.stringify(storedPolicy)) {
          delete next[key];
        } else {
          next[key] = { stockLocationId, variantId, stockPolicy };
        }
      });
      return next;
    });

  const hasStock = (variantId: ProductVariantId) =>
    locations.some(
      (location) => (getQuantity(location.id, variantId) ?? 0) > 0,
    );

  const changes = (): ProductStockChanges => ({
    balances: Object.values(quantityEdits),
    policies: Object.values(policyEdits),
  });

  return {
    locations,
    isLoading: enabled && !!productId && isPending,
    getQuantity,
    setQuantity,
    getPolicy,
    setPolicy,
    hasStock,
    changes,
  };
};

export type ProductVariantStock = ReturnType<typeof useProductVariantStock>;
